import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { TextHeading, TextBody } from '@ally/metronome-ui';
import {
  useAppCache,
  useAssessmentManager,
  useCustomersService,
  useSession,
  useTransmit,
  useConfig,
} from '../../../hooks';
import DefaultTemplate from '../../templates/DefaultTemplate';
import { LoadingSpinner } from '../../atoms';
import styled from 'styled-components';
import { muiAllyTheme } from '../../../utils';
import { useTransmitRef } from '../../../contexts/transmitContext';

const StyledTextHeading = styled(TextHeading)`
  color: ${muiAllyTheme.colors['plum-700']};
`;

interface IProps {}

const AuthenticatingPage: React.FC<IProps> = () => {
  const { isLoggedIn, loginWithTransmitToken } = useSession();

  const { getAssessment, inputValues } = useAssessmentManager();
  const { getACMStatus } = useCustomersService();
  const { getRelationship } = useAppCache();
  const [isWealthRelationship, setIsWealthRelationship] =
    useState<boolean>(false);
  const [isSecondary, setIsSecondary] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<
    'INIT' | 'INPROGRESS' | 'DONE'
  >('INIT');
  const { initTransmit } = useTransmit();
  const { transmitToken } = useTransmitRef();
  const {
    investConfig: { bankUrl },
  } = useConfig();

  useEffect(() => {
    const init = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const hasTicketId = !!searchParams.get('ticketID');

      // If not logged in and no SSO ticket, redirect to bank
      if (!hasTicketId && !isLoggedIn) {
        window.location.assign(bankUrl);
        return;
      }

      await initTransmit();
    };
    init();
  }, []);

  useEffect(() => {
    if (!isLoggedIn && transmitToken.access_token) {
      loginWithTransmitToken(transmitToken);
    }
  }, [transmitToken, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      const checkACMStatus = async () => {
        setLoadingStatus('INPROGRESS');
        const res = await getAssessment();
        setIsSecondary(!res);
        const relationshipList = await getACMStatus();
        setIsWealthRelationship(!!getRelationship('WEALTH', relationshipList));
        setLoadingStatus('DONE');
      };
      checkACMStatus();
    }
  }, [isLoggedIn]);

  return (
    <DefaultTemplate hideSubNav>
      <StyledTextHeading tag='h1' size='lg'>
        Authenticating
      </StyledTextHeading>
      <TextBody tag='strong' size='md'>
        Please Wait...
      </TextBody>
      <LoadingSpinner isLoading color='white' />
      {isLoggedIn && loadingStatus === 'DONE' && (
        <Navigate
          to={
            inputValues.assessmentStatus === 'SUBMITTED' ||
            (isSecondary && isWealthRelationship)
              ? '/'
              : '/prospect-assessment'
          }
        />
      )}
    </DefaultTemplate>
  );
};

export default AuthenticatingPage;
